/**
 * Help Center: "emails" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'emails-we-send',
    title: 'Emails you’ll get from Defera',
    summary: 'We email you when you book, when our team answers a request, and when your trip is paid off and your confirmation number is ready. Here’s what to expect.',
    keywords: ['notifications', 'messages from defera', 'what emails', 'inbox', 'updates', 'trip reminders', 'paid off email'],
    related: ['emails/booking-confirmation-email', 'emails/paid-off-email', 'emails/missing-email', 'emails/is-this-email-from-defera'],
    updated: '2026-10-05',
    body: `We email you at the important moments of your trip: when you book, when our team answers a request you've sent, and when your trip is paid off and your confirmation number is ready. A few other emails may come along the way, depending on your trip.

If an email ever goes missing, don't worry. Your booking details and the answers to your requests are also on your trip page in the app. Emails from your airline are separate from ours.

## Before you book

Two kinds of email can reach you while you're still deciding:

- **Your plan, when you ask for it.** At checkout, tap **Email me this plan** under the pay button. We send you the exact figures on your screen (what's due today, your payments and your trip total), with a link back to the same checkout.
- **Trip reminders.** If you leave a trip at the review step, you may get one or two reminder emails with the same figures: one about an hour later, and one the next day.

These emails don't charge you anything, and they don't hold a fare: fares can change until you book. Both carry an unsubscribe link for "Trip reminders". Here's [how unsubscribing works](/help/emails/email-preferences).

## When you book

Your booking confirmation arrives the moment you book. It comes from bookings@mail.defera.app, with the subject "[your booking reference] You're going to [your destination city]". It has your Defera booking reference, your flights, your travelers and your payment summary.

Your airline confirmation number isn't in this email, and that's normal. It's released once your trip is paid in full. Here's [what's in your booking confirmation email](/help/emails/booking-confirmation-email).

## Along the way

Depending on your trip, you may also get:

- **Answers to your requests.** When our team resolves a request you've sent, we email you the answer. It also appears on the request's card on your trip page.
- **"Will you still be taking this trip?"** If you booked more than two months ahead, you may get this courtesy check-in when your departure is about two months away. It isn't a warning, and if you're still going, there's nothing to do. Here's more about [the two-month check-in email](/help/changes-cancellations/still-taking-this-trip).
- **"Are you still taking this trip?"** If a payment on your trip keeps failing, we check in to ask whether you're still going, and how to catch up or switch what you've paid into Defera Credit. Here's more about [the "Are you still taking this trip?" email](/help/missed-payments/still-taking-this-trip-payments).
- **A faster route.** If you said yes to the Free Flight Upgrade question at checkout and we move you to a faster route, we email you. We never move you without telling you.
- **"You've been upgraded."** If our team arranges a complimentary cabin upgrade on your trip, we email you the moment it's arranged.

## When your trip is paid off

After your final payment, our team releases your airline confirmation number. When it's ready, we email you from bookings@mail.defera.app, with the subject "[your booking reference] Your trip has been paid off!".

The email confirms that your trip is paid in full, with no more payments due. Its **Get Confirmation Number** button opens your confirmation number. If your trip earned Defera Miles, the email tells you how many.

It comes when your confirmation number is released, not the moment your final payment goes through:

- **Final payment Monday through Thursday, or Friday morning (Pacific time):** expect it within 24 hours.
- **Final payment Friday afternoon or over the weekend:** expect it on Monday, or Tuesday at the latest.

Here's more about [the "Your trip has been paid off" email](/help/emails/paid-off-email).

## Your account

If you tap **Forgot password?** on the sign-in page and enter your email, we send you a secure link to reset your password.

## What about my payments?

Your payments are charged automatically to your card on file, on your payment schedule. Your trip in the app is the place to keep track of them: its Payment Schedule shows what's coming up, and your Trip Statement lists every payment. Here's [where to find receipts and due dates](/help/emails/payment-emails).

## Emails from your airline

Once your confirmation number is released, your airline also emails you directly, at the email address on your booking. Those emails come from the airline, not from us. [Which emails come from the airline?](/help/confirmation-and-travel/travel-emails-from-airline) explains the difference.

## Can't find an email?

Check your spam or junk folder, and make sure you're looking in the inbox for the email address on your Defera account. [I didn’t get an email from Defera](/help/emails/missing-email) has more places to look.

## Not sure an email is really from us?

Don't use its links. Open Defera yourself and check your trip instead. Here's [how to tell an email is really from Defera](/help/emails/is-this-email-from-defera).`,
  },
  {
    slug: 'booking-confirmation-email',
    title: 'Your booking confirmation email',
    summary: 'It arrives the moment you book, with your Defera booking reference, flights, travelers and payment summary. Your airline confirmation number comes later.',
    keywords: ['booking email', 'confirmation email', 'itinerary email', 'booking reference', 'you’re going to', 'add to calendar', 'receipt'],
    related: ['emails/missing-email', 'confirmation-and-travel/when-confirmation-number', 'payments/trip-statement', 'emails/emails-we-send'],
    updated: '2026-10-05',
    body: `Your booking confirmation email arrives the moment you book. It's your summary of the trip: your Defera booking reference, your flights, your travelers and your payment summary.

One thing you won't find in it yet is your airline confirmation number, and that's completely normal. Your confirmation number is released once your trip is paid in full, and we'll email you when it's ready.

## How do I find it in my inbox?

It comes from bookings@mail.defera.app, with the subject "[your booking reference] You're going to [your destination city]". If it isn't in your inbox, check your spam or junk folder, or search for the sender's address.

## What's in it?

- **Your Defera booking reference.** It's at the top, marked as not your airline confirmation number. Tap the copy icon beside it to open a small page where you can copy it in one tap.
- **A card for each flight.** Each card shows the times, airports, airline, flight number, aircraft and fare.
- **Your travelers.** You'll see every traveler detail you entered at checkout. Passport or ID numbers and Known Traveler Numbers show only their last 4 digits. The full numbers are in Flight Details on your trip.
- **Our seat promise.** Window seats, companions seated together and as close to the front as possible, when the fare allows.
- **Your payment summary and timeline.** The timeline shows each stage of your trip: booked, payments, paid in full with your airline confirmation number, then departure.
- **Defera Credit you used.** If you used Defera Credit at checkout, the email shows how much went toward your deposit and how much is set aside for upcoming payments. When credit covers a payment, your payment summary says "Covered by credit" and shows the first payment your card is actually charged. If credit covers every payment, it shows $0.00 due and says nothing is charged to your card.
- **Airline credits and gift cards.** Any airline travel credit or gift card you applied shows only its last 4 digits. It's marked "pending verification" until our team verifies it with the airline.
- **An Add to calendar button.** It adds every flight to your calendar at the right local times. It works with Apple Calendar, Outlook and most calendar apps.
- **Answers to common questions.** These cover cancelling, including the [24-hour deposit refund](/help/changes-cancellations/cancel-within-24-hours) and Defera Credit on select bookings. They also cover how Defera Credit works, **Change Flight**, paying early, adding seats and bags, **Change Schedule** and **Update Card**.

## Is my booking reference the same as my confirmation number?

No, they're two different codes:

- **Your Defera booking reference** is how we find your trip at Defera. It's at the top of this email, and it's handy to have when you contact us.
- **Your airline confirmation number** is the code the airline uses to find your reservation, and you'll use it to check in. It's released once your trip is paid in full, and then it appears on your trip page.

To see how long the release takes, read [When do I get my confirmation number?](/help/confirmation-and-travel/when-confirmation-number)

## What if something in it looks wrong?

When the email arrives, take a moment to check each traveler's name and details. Each name should match the traveler's government ID. If something doesn't look right, chat with the [Defera Concierge](/support), our AI concierge. It can send a request to our team to take a look.

## Can't find it?

Everything in the email is also on your trip page: open **Trips** and tap the trip. Check your spam or junk folder too, and see [I didn’t get an email from Defera](/help/emails/missing-email) for more places to look.

> [!TIP] Need a document to print or share? Download your Trip Statement: open the trip in **Trips** and tap the download icon in the trip's header row.`,
  },
  {
    slug: 'missing-email',
    title: 'I didn’t get an email from Defera',
    summary: 'Check spam or junk, and the inbox for the email address on your account. Your booking details and answers to your requests are on your trip page too.',
    keywords: ['no email', 'email not received', 'spam', 'junk folder', 'resend email', 'wrong email address', 'confirmation email missing'],
    related: ['emails/booking-confirmation-email', 'account/update-profile', 'payments/trip-statement', 'contact/how-to-contact'],
    updated: '2026-10-23',
    body: `Start with your spam or junk folder. Then make sure you're checking the inbox for the email address on your Defera account.

You may not need the email at all. Everything in your booking confirmation is on your trip page in the app, and so are the answers to any requests you've sent us.

## What should I check first?

1. **Look in spam or junk.** Your booking confirmation comes from bookings@mail.defera.app. Its subject starts with your booking reference, then "You're going to" and your destination city. Searching your inbox for the sender or for "You're going to" can turn it up.
2. **Check which address you used.** Open your avatar menu and tap **Profile**. Your email address is in Profile Details. It's read-only there, because it's the address you sign in with. If you have more than one email account, look in that one.
3. **Open your trip page.** Go to **Trips** and tap the trip. Your booking details are there, along with any requests you've sent us and their answers.

## Which email are you waiting for?

### Your booking confirmation

It's sent the moment you book. If you can't find it, everything in it is also on your trip page.

### Your "paid off" email

This one doesn't arrive the moment your final payment goes through. It comes when our team releases your confirmation number, and it's also from bookings@mail.defera.app. Until then, your trip page shows a "Confirmation Number Release" request.

Here's when to expect it:

- **Final payment Monday through Thursday, or Friday morning (Pacific time):** within 24 hours.
- **Final payment Friday afternoon or over the weekend:** on Monday, or Tuesday at the latest.

If it's been longer, see [I’ve paid off my trip. Where’s my confirmation number?](/help/confirmation-and-travel/paid-off-no-confirmation)

### An answer to a request

When our team resolves a request, the answer appears on the request's card on your trip page, and we email it to you too. So even if the email goes missing, the card still has the answer. For an e-gift card, the status card is on your **Trips** page and on the **Rewards** page.

### A password reset email

When you tap **Forgot password?** on the sign-in page and enter your email, we send you a secure reset link. If it isn't in your inbox, check spam or junk, and make sure you entered the address you sign in with.

## Can you send my trip to a different address?

We can't send trip documents to an address that isn't on your account. But you can share them yourself: every trip has a downloadable [Trip Statement](/help/payments/trip-statement). Open **Trips**, tap the trip, then tap the download icon in the trip's header row. You can forward the PDF to anyone who needs it, like an employer or a travel companion.

If you need the email address on your account changed, chat with the Defera Concierge, our AI concierge. It can send a request to our team.

## Still can't find it?

Contact us and tell us which email you're missing, plus your booking reference if you have one:

- Chat with the [Defera Concierge](/support) any time.
- Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}). The same AI concierge answers by voice, 24/7.
- Send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'email-preferences',
    title: 'Unsubscribing from Defera emails',
    summary: 'Reminder emails about a trip you didn’t finish booking have an unsubscribe link for “Trip reminders”. For any other email, contact us and tell us which one.',
    keywords: ['unsubscribe', 'stop emails', 'opt out', 'email settings', 'email preferences', 'too many emails', 'trip reminders'],
    related: ['emails/emails-we-send', 'emails/missing-email', 'account/update-profile'],
    updated: '2026-10-23',
    body: `To stop reminder emails about a trip you didn't finish booking, use the unsubscribe link in one of those emails. It's for "Trip reminders". For any other email from us, contact us and tell us which one it is.

Emails about a trip you've booked are a little different. They keep you up to date on that booking: your booking confirmation, the answers to your requests, and the email that tells you your confirmation number is ready.

## What are trip reminders?

If you start booking a trip but leave at the review step, you may get one or two reminder emails with the figures you saw: one about an hour later, and one the next day. For example, if you leave checkout at 2 PM, a reminder may arrive around 3 PM, and another the next day.

You can also ask for those figures yourself. Tap **Email me this plan** at checkout, and we send you the exact figures on your screen, with a link back to the same checkout.

Both kinds of email carry an unsubscribe link for "Trip reminders".

## Do these emails hold my fare?

No. Nothing is charged by these emails, and they don't hold a fare: fares can change until you book. Your trip price is secured once your booking is approved.

## What about emails about my booking?

Every new booking gets a confirmation email. When our team resolves a request you've sent, you get the answer by email. Both are also on your trip page: open **Trips** and tap the trip. Here's more about [the emails you'll get from Defera](/help/emails/emails-we-send).

## Can I change the email address you use?

Your email address is shown in Profile Details on your **Profile**, which you open from your avatar menu. It's read-only there, because it's the address you sign in with. If you need it changed, chat with the [Defera Concierge](/support), our AI concierge. It can send a request to our team.

## What about any other email?

Contact us and tell us which one:

- Chat with the [Defera Concierge](/support) any time.
- Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}). The same AI concierge answers by voice, 24/7.
- Send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'payment-emails',
    title: 'Payment receipts and due dates',
    summary: 'For a receipt, download your Trip Statement from your trip: it lists what you’ve paid and what’s coming up. Your Payment Schedule shows when the next one is due.',
    keywords: ['receipt', 'payment confirmation', 'payment history', 'proof of payment', 'payment reminder', 'upcoming payment', 'statement'],
    related: ['payments/trip-statement', 'payments/view-payment-schedule', 'missed-payments/payment-reminders', 'missed-payments/payment-failed'],
    updated: '2026-10-05',
    body: `For a receipt or proof of payment, download your Trip Statement from your trip in the app. It lists what you've paid, what's coming up and anything past due. To see when your next payment is due, open your trip in **Trips** and check the Payment Schedule.

Your payments are charged automatically to your card on file, which is the card saved for your trip's payments. So there's nothing you need to do on the day, and your trip in the app is your record of every payment.

## How do I get a receipt?

Your Trip Statement works as your receipt. Here's how to get it:

1. **Open your trip.** Go to **Trips** and tap the trip.
2. **Tap the download icon.** It's in the trip's header row, next to the message and call support buttons.
3. **Save, print or share it.** It's a print-ready PDF, named after your booking reference.

The statement works on every trip, at every status. It has your full flight itinerary, the passenger names, the pricing summary and your payment plan with its history: paid, upcoming and any past-due payments.

Once your trip is paid in full, the statement shows your airline confirmation number too. Before that, it shows your Defera booking reference. Here's more about [your Trip Statement](/help/payments/trip-statement).

## What about my booking confirmation email?

It has a payment summary from the day you booked. When Defera Credit covers a payment, it says "Covered by credit" and shows the first payment your card is actually charged. If credit covers every payment, it shows $0.00 due. For the latest picture, use your Trip Statement or your trip's Payment Schedule.

## When is my next payment due?

Open **Trips**, tap the trip and look at the Payment Schedule. It shows your upcoming payments, and your Trip Statement lists them too.

Because payments are charged automatically, it's worth keeping your card up to date. If your card expires or you get a new one, tap **Update Card** in the Payment Schedule section. Your new card is used for all your future payments.

## What if a payment doesn't go through?

A missed payment doesn't cancel your trip on the spot. Your trip shows a past-due banner, and you can catch up from **Make a Payment** as soon as you have the funds. It's best not to leave it, because a plan left past due for a long time can pause. Here's [what happens if a payment doesn't go through](/help/missed-payments/payment-failed).

## A question about a charge?

If you see a charge you don't recognize, [A charge I don’t recognize](/help/payments/unrecognized-charge) walks through what to check. For anything else, chat with the Defera Concierge, our AI concierge.`,
  },
  {
    slug: 'paid-off-email',
    title: 'The "Your trip has been paid off" email',
    summary: 'It means your trip is paid in full and your airline confirmation number is ready. Tap Get Confirmation Number in the email, or open your trip in Trips.',
    keywords: ['paid in full', 'final payment', 'trip completed', 'last payment', 'confirmation number', 'what happens next'],
    related: ['confirmation-and-travel/paid-off-no-confirmation', 'confirmation-and-travel/when-confirmation-number', 'rewards/earning-miles', 'confirmation-and-travel/check-in'],
    updated: '2026-10-23',
    body: `This email means your trip is paid in full and your airline confirmation number is ready. There are no more payments to make. Tap **Get Confirmation Number** in the email to see your number, or open your trip in **Trips**.

If your trip earned Defera Miles, the email also tells you how many. From here, you'll use your confirmation number with your airline, including to check in.

## What is a confirmation number?

Your confirmation number is the code the airline uses to find your reservation. It's usually a short mix of letters and numbers, and some airlines call it a record locator.

It isn't the same as your Defera booking reference, the code at the start of this email's subject line. Your booking reference is how we find your trip at Defera. Your confirmation number is what the airline needs.

## How do I know it's from Defera?

It comes from bookings@mail.defera.app, the same address as your booking confirmation. The subject reads "[your booking reference] Your trip has been paid off!".

If you're ever unsure about an email, open Defera yourself and check your trip. Your confirmation number is on your trip page too.

## When does it arrive?

It comes when our team releases your confirmation number, not the moment your final payment goes through. Here's what happens:

1. **You make your final payment.** As soon as it goes through, a "Confirmation Number Release" request is sent to our team for you automatically. It shows as a card on your trip page.
2. **Our team releases your confirmation number.** They work Monday through Friday, Pacific time.
3. **This email arrives.** Your trip now shows as completed, and your confirmation number is on your trip page.
4. **The airline emails you too.** You'll also hear from the airline directly, at the email address on your booking.

How long step 2 takes depends on when your final payment goes through:

- **Monday through Thursday, or Friday morning (Pacific time):** expect this email within 24 hours.
- **Friday afternoon or over the weekend:** expect it on Monday, or Tuesday at the latest.

For example, if your last payment goes through on a Thursday morning, you can expect this email by Friday morning. If it goes through on a Saturday, look for it on Monday, or Tuesday at the latest.

## Paid in full, but no email yet?

First, check your spam or junk folder, and look at your trip page. If the "Confirmation Number Release" card is there, your request is with our team.

If it's been longer than the times above, or you don't see the card, open your trip in **Trips** and tap **Finished paying for your trip?** in the payment schedule. It sends the request if one wasn't sent, or confirms that yours is already being handled.

Still waiting after that? [I’ve paid off my trip. Where’s my confirmation number?](/help/confirmation-and-travel/paid-off-no-confirmation) walks through what to do next.

## Where do I find my confirmation number?

1. **Open it.** Tap **Get Confirmation Number** in the email, or open **Trips** and tap your trip.
2. **Find it on your trip page.** Your confirmation number is shown there, and from now on your Trip Statement shows it too.
3. **Use it with your airline.** You'll need it to check in, and to look up or manage your trip on the airline's website or app.

Here's more on [using your confirmation number with the airline](/help/confirmation-and-travel/use-confirmation-with-airline).

## What about my Miles?

Your trip earns its Defera Miles now, when it's completed. You earn 2 Miles for every $1 of the final amount you paid, and more with a higher Defera Status tier. Miles aren't earned on any part of the trip you paid with Miles.

Your balance is on the [Rewards](/rewards) page. The Miles from this trip go toward your next trip, or you can turn them into an e-gift card.

## Questions?

Chat with the [Defera Concierge](/support), our AI concierge, any time. You can also call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'is-this-email-from-defera',
    title: 'How to tell an email is really from Defera',
    summary: 'Not sure an email is from us? Skip its links, open Defera yourself and check your trip. Our booking and paid-off emails come from bookings@mail.defera.app.',
    keywords: ['phishing', 'scam', 'fake email', 'suspicious email', 'sender address', 'spoofing', 'is this legit'],
    related: ['account/account-security', 'emails/emails-we-send', 'emails/booking-confirmation-email', 'emails/paid-off-email'],
    updated: '2026-10-23',
    body: `If you're not sure an email is really from us, don't tap its links or buttons. Instead, open Defera yourself, in the app or by typing defera.app into your browser. Then sign in and check your trip: your booking details, your payments and the answers to your requests are all there.

It's smart to double-check. Scam emails often copy a company's look and push you to act fast, and a quick look at your trip in the app shows you what's real. Be wary of any message that asks for your password or your full card number.

## How do I check an email?

1. **Open Defera yourself.** Use the app, or type defera.app into your browser, and sign in. Don't go through the email's links.
2. **Open the trip.** Go to **Trips** and tap the trip the email is about.
3. **Compare.** Your booking details, your Payment Schedule and any requests you've sent us, with their answers, are all on the trip page.
4. **Act in the app, not the email.** If the email asks you to do something, do it in the app instead. If what it says isn't on your trip page, contact us before you act on it.

## What do real Defera emails look like?

- **Your booking confirmation** comes from bookings@mail.defera.app, with the subject "[your booking reference] You're going to [your destination city]". It shows only the last 4 digits of passport, ID and Known Traveler numbers.
- **"Your trip has been paid off!"** comes from the same address once our team releases your confirmation number. Its **Get Confirmation Number** button opens your number in your Defera account, and you'll find the same number by opening your trip yourself.
- **Answers to your requests** also appear on the request's card on your trip page, so you can check one there.
- **"Will you still be taking this trip?"** is a real courtesy check-in that may arrive when your departure is about two months away. If you're still going, there's nothing to do. Here's more about [the two-month check-in email](/help/changes-cancellations/still-taking-this-trip).
- **"Are you still taking this trip?"** is a real check-in too, sent when a payment on your trip keeps failing. Check it against the Payment Schedule on your trip page. Here's more about [the "Are you still taking this trip?" email](/help/missed-payments/still-taking-this-trip-payments).
- **"You've been upgraded"** is real too. When our team arranges a complimentary cabin upgrade, you'll also see a "Complimentary Upgrade" card on your trip page.

## What about card details and passwords?

- **Change your card only in the app.** The only place to change the card on your trip is **Update Card**, in your trip's Payment Schedule section. Don't send card numbers by email.
- **Reset emails come when you ask.** A password reset email comes after you tap **Forgot password?** on the sign-in page. If you get one you didn't ask for, don't use its link.
- **Keep your password to yourself.** Don't share it with anyone.

## What if I already used a link?

We know that's a worrying moment. If you entered your Defera password on a page you're not sure about, reset it with **Forgot password?** on the sign-in page. Then contact us so our team can take a look. If you entered card details, contact your bank too.

## How do I report a fake Defera email?

Forward it, or a screenshot of it, to [{{SECURITY_EMAIL}}](mailto:{{SECURITY_EMAIL}}). Please leave out your password and full card number. [Avoiding scams](/security/avoiding-scams), in our Security Center, also covers calls, texts and fake websites.

## Still not sure? Ask us

- Chat with the [Defera Concierge](/support), our AI concierge, any time.
- Call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}). The same AI concierge answers by voice, 24/7. Before it shares any account details, it asks for your booking reference (or your account email) and the passenger's name.
- Send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
];
